/**
 * Header Component
 * Application header with logo, search, theme selector, and profile menu
 */

import { AppBar, Toolbar, Typography, Box, IconButton } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import SearchInput from './SearchInput';
import ProfileMenu from './ProfileMenu';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';

interface HeaderProps {
  onMenuClick?: () => void;
  onSearch?: (query: string) => void;
}

export default function Header({ onMenuClick, onSearch }: HeaderProps) {
  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        borderRadius: 0,
        overflow: 'hidden',
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 1px 3px rgba(0,0,0,0.3)'
            : '0 1px 3px rgba(0,0,0,0.12)',
      }}
    >
      <Toolbar sx={{ borderRadius: 0 }}>
        {/* Menu button (for mobile) */}
        <IconButton
          color="inherit"
          aria-label="abrir menú"
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo and title */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.5px',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Remote Assist
          </Typography>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.5px',
              display: { xs: 'block', sm: 'none' },
            }}
          >
            RA
          </Typography>
        </Box>

        {/* Spacer + Search — always in the toolbar, shrinks to fit available space */}
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            mx: { xs: 1, sm: 3 },
            display: 'flex',
          }}
        >
          <SearchInput placeholder="Buscar agentes, enrollments..." onSearch={onSearch} />
        </Box>

        {/* Right side actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {/* Notifications */}
          <NotificationsBell />

          {/* Theme selector removed: moved to Profile menu */}

          {/* Profile menu */}
          <ProfileMenu />
        </Box>
      </Toolbar>

      {/* Mobile search bar removed — search now lives in the toolbar above
          and shrinks with the available space, so we never wrap to a 2nd row. */}
    </AppBar>
  );
}
